import { Slot } from 'radix-ui';
import type { ComponentPropsWithRef, MouseEvent, ReactNode } from 'react';
import { buttonClasses } from './button-classes';
import type { ButtonSize, ButtonVariant } from './button-classes';
import { WhatsAppGlyph } from './glyphs';
import { Spinner } from './Spinner';

type NativeButtonProps = Omit<ComponentPropsWithRef<'button'>, 'children'>;

/** Icon-only variants have no visible text, so an accessible name is mandatory (checked by the compiler). */
type AccessibleName = { 'aria-label': string } | { 'aria-labelledby': string };

type ButtonVariantProps =
  | { variant?: Exclude<ButtonVariant, 'icon' | 'whatsapp'> }
  | ({ variant: 'icon' | 'whatsapp' } & AccessibleName);

export type ButtonProps = NativeButtonProps &
  ButtonVariantProps & {
    size?: ButtonSize;
    /** Round, icon-only shape (implied by `icon` / `whatsapp`). */
    round?: boolean;
    /**
     * Shows a spinner in place of the label while keeping the button's width, sets `aria-busy`, and
     * ignores clicks — but stays focusable so keyboard users do not lose their place.
     */
    loading?: boolean;
    /**
     * Render the single child element (e.g. a router `<Link>`) with the button's look and props
     * instead of a `<button>` — Radix `Slot` composition.
     */
    asChild?: boolean;
    children?: ReactNode;
  };

/**
 * The SBG button. Variants: `primary` (bone pill — the "Shop" look) · `glass` · `outline` (brass) ·
 * `ghost` · `icon` (round glass) · `whatsapp` (round, brand green, white glyph). Every size keeps a
 * ≥ 44 px target; hover styles only apply on real hover devices; pressing scales to .98.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  round,
  loading = false,
  asChild = false,
  disabled = false,
  type = 'button',
  className,
  children,
  onClick,
  ...rest
}: ButtonProps) {
  const inactive = disabled || loading;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (inactive) {
      event.preventDefault(); // also stops a busy submit button from submitting twice
      return;
    }
    onClick?.(event);
  };

  const content = children ?? (variant === 'whatsapp' ? <WhatsAppGlyph /> : null);
  const spinner = loading ? (
    <span className="btn__spinner" aria-hidden="true">
      <Spinner size={size === 'lg' ? 24 : 20} />
    </span>
  ) : null;

  const shared = {
    ...rest,
    className: buttonClasses({ variant, size, round, className }),
    onClick: handleClick,
    'aria-busy': loading || undefined,
    'aria-disabled': inactive || undefined,
  };

  if (asChild) {
    return (
      <Slot.Root {...shared}>
        <Slot.Slottable>{content}</Slot.Slottable>
        {spinner}
      </Slot.Root>
    );
  }

  return (
    <button type={type} disabled={disabled} {...shared}>
      {content}
      {spinner}
    </button>
  );
}
